import { useMemo } from "react"
import { Alert, Card, Space, Tag } from "antd"
import type { EquipmentContextQuestionnaire as Questionnaire } from "../../types/regulatory"
import { EN1176QuestionnaireWidget } from "./EN1176QuestionnaireWidget"
import { useRegulatoryAnswers } from "./useRegulatoryAnswers"
import type { ICloudRegulatoryControlPoint } from "../../types"
import { legacyControls, regulatoryUuid } from "../../utils/regulatoryPresentation"
export function EquipmentContextQuestionnaire({
  data,
  points,
  answers,
  readOnly = false,
}: {
  data: Questionnaire
  points: ICloudRegulatoryControlPoint[]
  answers: ReturnType<typeof useRegulatoryAnswers>
  readOnly?: boolean
}) {
  const groups = useMemo(
    () =>
      data.configuration.contexts.map((context) => ({
        context,
        points: points.filter((p) => p.AnswerContextUUID === context.uuid),
        shared: data.controls.filter(
          (p) => p.displayContextUuid === context.uuid && p.answerContextUuid !== context.uuid,
        ),
      })),
    [data, points],
  )
  return (
    <Space direction="vertical" style={{ width: "100%" }} size="large">
      <Alert
        type="info"
        message={`${data.ownedControlCount} contrôles uniques · ${data.configuration.contexts.filter((c) => c.kind === "component").length} composants`}
        description="Chaque composant conserve ses propres réponses. Les contrôles explicitement partagés se renseignent dans l’ensemble de l’équipement et sont comptés une seule fois."
      />
      {groups.map(({ context, points: owned, shared }) => {
        const contextAnswers = Object.fromEntries(
          Object.entries(answers.answers).filter(
            ([, a]) => regulatoryUuid(a.context_uuid) === context.uuid,
          ),
        )
        const states = Object.fromEntries(
          Object.entries(answers.states).filter(([key]) => key.startsWith(`${context.uuid}/`)),
        )
        const unresolved =
          !context.assignments.length ||
          context.assignments.some(
            (a) => !a.templateRevisionUuid || !data.resolvedAssignments[a.uuid],
          )
        const controls = owned.length
          ? owned
          : unresolved
            ? legacyControls(contextAnswers).map((p) => ({ ...p, ContextLabel: context.label }))
            : owned
        return (
          <section
            id={`equipment-context-${context.uuid}`}
            key={context.uuid}
            className="reg-equipment-context"
          >
            <Card size="small" title={context.label}>
              <Space wrap>
                <Tag>{context.kind === "whole" ? "Ensemble" : "Composant"}</Tag>
                <Tag>{owned.length} contrôles propres</Tag>
                <Tag>{shared.length} contrôles partagés</Tag>
              </Space>
              {unresolved && (
                <Alert
                  type="warning"
                  message="Version historique inconnue"
                  description="Les anciennes réponses restent consultables. L’adoption d’une révision dans la configuration doit être explicite."
                />
              )}
              {!context.assignments.length && (
                <Alert type="info" message="Aucun modèle affecté à ce contexte" />
              )}
            </Card>
            {shared.length > 0 && (
              <Card size="small" title="Constats communs réutilisés">
                <p>Ces résultats sont renseignés dans leur contexte d’origine.</p>
                {Array.from(new Set(shared.map((c) => c.answerContextUuid))).map((owner) => (
                  <a key={owner} href={`#equipment-context-${owner}`}>
                    Voir{" "}
                    {data.configuration.contexts.find((c) => c.uuid === owner)?.label ??
                      "le constat partagé"}{" "}
                    ({shared.filter((c) => c.answerContextUuid === owner).length} contrôles)
                  </a>
                ))}
              </Card>
            )}
            {(controls.length > 0 || unresolved) && (
              <EN1176QuestionnaireWidget
                guardNavigation={false}
                scopeKey={context.uuid}
                title={context.label}
                controlPoints={controls}
                answers={contextAnswers}
                saveStates={states}
                onAnswersChange={answers.save}
                onAnswerBlur={answers.flush}
                retry={answers.retry}
                reload={answers.reload}
                loading={answers.loading}
                error={answers.error}
                connected={answers.connected}
                readOnly={readOnly || unresolved}
              />
            )}
          </section>
        )
      })}
    </Space>
  )
}
